"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties } from "react";
import { AnimatePresence, motion } from "motion/react";
import type { Variants } from "motion/react";
import { Play, X } from "lucide-react";
import { cn } from "@/lib/utils";

// ── Data ────────────────────────────────────────────────────────────────────

interface VideoItem {
  title: string;
  duration: string;
  youtubeId?: string;
  startSeconds?: number;
  thumbnailSrc?: string;
  thumbnailPosition?: string;
}

const videos: VideoItem[] = [
  {
    title: "Our Vision for Digital Gold",
    duration: "3:24",
    youtubeId: "q8GTHCkwYFM",
  },
  {
    title: "Building Trust in Digital Finance",
    duration: "2:15",
    youtubeId: "pDpbQ83U338",
    thumbnailSrc: "/video-trust-thumb.jpg",
    thumbnailPosition: "center 25%",
  },
  {
    title: "Why Physical Gold Matters",
    duration: "4:10",
    youtubeId: "oGon-ZAw3Os",
    startSeconds: 1,
  },
];

// ── Motion ──────────────────────────────────────────────────────────────────

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.2, 0, 0, 1] },
  },
};

const stagger: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

const pulse: Variants = {
  idle: {
    scale: [1, 1.1, 1],
    transition: { duration: 2, repeat: Infinity, ease: "easeInOut" },
  },
};

// ── Video Card ──────────────────────────────────────────────────────────────

function VideoCard({
  video,
  onPlay,
}: {
  video: VideoItem;
  onPlay: (video: VideoItem) => void;
}) {
  const canPlay = Boolean(video.youtubeId);
  const thumbnailSrc =
    video.thumbnailSrc ??
    (video.youtubeId
      ? `https://i.ytimg.com/vi/${video.youtubeId}/hqdefault.jpg`
      : undefined);
  const thumbnailStyle = thumbnailSrc
    ? ({
        "--video-thumbnail": `url("${thumbnailSrc}")`,
        "--video-thumbnail-position": video.thumbnailPosition ?? "center",
      } as CSSProperties)
    : undefined;

  return (
    <motion.div
      variants={fadeUp}
      whileHover={{ scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      onClick={() => canPlay && onPlay(video)}
      role={canPlay ? "button" : undefined}
      tabIndex={canPlay ? 0 : undefined}
      aria-label={canPlay ? `Play video: ${video.title}` : undefined}
      onKeyDown={(e) => {
        if (canPlay && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onPlay(video);
        }
      }}
      className={cn(
        "group relative aspect-video overflow-hidden rounded-card shadow-lg",
        "transition-shadow duration-300 hover:shadow-2xl",
        canPlay ? "cursor-pointer" : "cursor-default"
      )}
    >
      {/* Background */}
      <div className="absolute inset-0 bg-primary-900" />
      {thumbnailSrc ? (
        <div
          className="absolute inset-0 bg-[image:var(--video-thumbnail)] bg-cover bg-[position:var(--video-thumbnail-position)] transition-transform duration-500 ease-out group-hover:scale-105"
          style={thumbnailStyle}
          aria-hidden="true"
        />
      ) : null}

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-primary-900/95 via-primary-900/45 to-primary-900/10" />
      <div className="absolute inset-0 bg-primary-900/10 transition-colors duration-300 group-hover:bg-primary-900/0" />

      {/* Subtle cinematic grain texture */}
      <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22100%22%20height%3D%22100%22%3E%3Cfilter%20id%3D%22noise%22%3E%3CfeTurbulence%20baseFrequency%3D%220.9%22%20numOctaves%3D%224%22%20stitchTiles%3D%22stitch%22%2F%3E%3C%2Ffilter%3E%3Crect%20width%3D%22100%25%22%20height%3D%22100%25%22%20filter%3D%22url(%23noise)%22%20opacity%3D%220.5%22%2F%3E%3C%2Fsvg%3E')]" />

      {/* Play button */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          variants={pulse}
          animate="idle"
          className={cn(
            "flex h-14 w-14 items-center justify-center rounded-full",
            "bg-white/15 backdrop-blur-sm border border-white/20",
            "transition-colors duration-300 group-hover:bg-white/25"
          )}
        >
          <Play className="ml-0.5 h-5 w-5 fill-white text-white" />
        </motion.div>
      </div>

      {/* Bottom info */}
      <div className="absolute bottom-0 left-0 right-0 p-5">
        <div className="flex items-end justify-between gap-3">
          <h3 className="text-sm font-semibold leading-snug text-white sm:text-base">
            {video.title}
          </h3>
          <span
            className={cn(
              "shrink-0 rounded-full bg-white/15 backdrop-blur-sm",
              "px-2.5 py-0.5 text-xs font-medium text-white/90 border border-white/10"
            )}
          >
            {video.duration}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

// ── Video Modal ─────────────────────────────────────────────────────────────

function VideoModal({
  video,
  onClose,
}: {
  video: VideoItem | null;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!video) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [video, onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {video ? (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.2, 0, 0, 1] }}
          role="dialog"
          aria-modal="true"
          aria-label={video.title}
        >
          <motion.div
            className="absolute inset-0 bg-primary-900/80 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden="true"
          />

          <motion.div
            className="relative z-10 w-full max-w-4xl"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.2, 0, 0, 1] }}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close video"
              className="absolute -top-11 right-0 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 sm:-top-12"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="relative aspect-video w-full overflow-hidden rounded-card bg-primary-900 shadow-xl">
              {video.youtubeId ? (
                <iframe
                  className="absolute inset-0 h-full w-full"
                  src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&rel=0${
                    video.startSeconds ? `&start=${video.startSeconds}` : ""
                  }`}
                  title={video.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              ) : null}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body
  );
}

// ── Section ─────────────────────────────────────────────────────────────────

export function VideoShowcase() {
  const [activeVideo, setActiveVideo] = useState<VideoItem | null>(null);

  return (
    <section className="bg-neutral-0 border-b border-[#e3ecfb] pb-16 pt-16 sm:pb-20 sm:pt-20 lg:pb-24 lg:pt-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        {/* Header */}
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="mb-12 max-w-2xl"
        >
          <motion.p
            variants={fadeUp}
            className="text-xs font-semibold uppercase tracking-widest text-primary-500 mb-3"
          >
            From Our CEO
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="text-[clamp(1.75rem,3.5vw,2.5rem)] font-semibold leading-[1.15] tracking-[-0.015em] text-primary-900 mb-4"
          >
            Hear it from our leadership
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="text-base sm:text-lg text-text-body leading-relaxed"
          >
            Transparency is at the heart of everything we do. Get a firsthand
            look at the vision, values, and commitment driving our team forward.
          </motion.p>
        </motion.div>

        {/* Video Grid — single row, three equal videos */}
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-40px" }}
          className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
        >
          {videos.map((video) => (
            <VideoCard key={video.title} video={video} onPlay={setActiveVideo} />
          ))}
        </motion.div>
      </div>

      <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />
    </section>
  );
}
